<template>
  <div class="admin-shell">

    <!-- ══ SIDEBAR ══ -->
    <aside class="sidebar" :class="{ 'sidebar--open': sidebarOpen }">
      <div class="sidebar__brand">
        <div class="brand-icon"><i class="fas fa-shield-alt"></i></div>
        <span class="brand-name">Administrare</span>
      </div>

      <nav class="sidebar__nav">
        <button
          v-for="tab in tabs" :key="tab.key"
          class="nav-item"
          :class="{ 'nav-item--active': activeTab === tab.key }"
          @click="activeTab = tab.key; sidebarOpen = false"
        >
          <span class="nav-item__icon"><i :class="tab.icon"></i></span>
          <span class="nav-item__label">{{ tab.label }}</span>
          <span v-if="tab.badge" class="nav-item__badge">{{ tab.badge }}</span>
        </button>
      </nav>

      <button class="sidebar__logout" @click="logoutAdmin">
        <i class="fas fa-sign-out-alt"></i>
        <span>Logout</span>
      </button>
    </aside>

    <!-- overlay for mobile -->
    <div class="sidebar-overlay" v-if="sidebarOpen" @click="sidebarOpen = false"></div>

    <!-- ══ MAIN ══ -->
    <div class="main">

      <!-- Topbar -->
      <header class="topbar">
        <button class="topbar__hamburger" @click="sidebarOpen = !sidebarOpen">
          <i class="fas fa-bars"></i>
        </button>
        <h1 class="topbar__title">{{ currentTabLabel }}</h1>
        <div class="topbar__right">
          <div class="topbar__avatar">A</div>
        </div>
      </header>

      <!-- Content -->
      <main class="content">

        <!-- ── HOME ── -->
        <div v-if="activeTab === 'home'" class="fade-in">
          <div class="stats-grid">
            <div class="stat-card" v-for="stat in statsList" :key="stat.label">
              <div class="stat-card__icon" :style="{ background: stat.color }">
                <i :class="stat.icon"></i>
              </div>
              <div class="stat-card__body">
                <div class="stat-card__value">{{ stat.value }}</div>
                <div class="stat-card__label">{{ stat.label }}</div>
              </div>
            </div>
          </div>

          <div class="section-header">
            <h2 class="section-title">Administratori</h2>
            <button class="btn btn--primary btn--sm" @click="showAddAdminModal = true">
              <i class="fas fa-plus"></i> Adaugă
            </button>
          </div>

          <div class="table-card">
            <table class="data-table">
              <thead>
                <tr><th>Username</th><th style="width:110px">Acțiuni</th></tr>
              </thead>
              <tbody>
                <tr v-for="admin in adminList" :key="admin.id">
                  <td><span class="fw">{{ admin.username }}</span></td>
                  <td>
                    <div class="action-btns">
                      <button @click="editAdmin(admin)" class="icon-btn icon-btn--ghost"><i class="fas fa-pen"></i></button>
                      <button @click="deleteAdmin(admin.id)" class="icon-btn icon-btn--danger"><i class="fas fa-trash"></i></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── NEWS ── -->
        <div v-else-if="activeTab === 'news'" class="fade-in">
          <div class="two-col">
            <!-- Form -->
            <div class="panel">
              <div class="panel__header"><i class="fas fa-plus-circle"></i> Anunț nou</div>
              <div class="panel__body">
                <form @submit.prevent="createNewsPost">
                  <div class="field">
                    <label class="field__label">Titlu</label>
                    <input v-model="form.title" class="field__input" placeholder="Titlul anunțului" required />
                  </div>
                  <div class="field">
                    <label class="field__label">Conținut</label>
                    <textarea v-model="form.content" class="field__input field__textarea" placeholder="Descriere..." required></textarea>
                  </div>
                  <div class="field">
                    <label class="field__label">Imagine <span class="opt">(opțional)</span></label>
                    <input type="file" @change="onImageChange" accept="image/*" class="field__file" />
                  </div>
                  <div class="field">
                    <label class="field__label">Fișiere atașate</label>
                    <input type="file" multiple @change="onFilesChange" class="field__file" />
                  </div>
                  <button type="submit" class="btn btn--primary btn--full">
                    <i class="fas fa-upload"></i> Publică anunțul
                  </button>
                </form>
              </div>
            </div>

            <!-- List -->
            <div class="panel">
              <div class="panel__header"><i class="fas fa-newspaper"></i> Anunțuri existente</div>
              <div v-if="newsList.length" class="table-card table-card--flush">
                <table class="data-table">
                  <thead>
                    <tr><th>Titlu</th><th>Status</th><th>Data</th><th style="width:80px">Acțiuni</th></tr>
                  </thead>
                  <tbody>
                    <tr v-for="post in newsList" :key="post.id">
                      <td class="fw">{{ post.title }}</td>
                      <td><span class="badge" :class="post.published ? 'badge--green' : 'badge--gray'">{{ post.published ? 'Publicat' : 'Ascuns' }}</span></td>
                      <td class="muted small">{{ formatDate(post.created_at) }}</td>
                      <td>
                        <div class="action-btns">
                          <button @click="openEditNewsModal(post)" class="icon-btn icon-btn--ghost"><i class="fas fa-pen"></i></button>
                          <button @click="deleteNews(post.id)" class="icon-btn icon-btn--danger"><i class="fas fa-trash-alt"></i></button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="empty-state">
                <i class="fas fa-inbox"></i>
                <p>Niciun anunț momentan.</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ── CHAT ── -->
        <div v-else-if="activeTab === 'chat'" class="fade-in">
          <div class="chat-layout">
            <!-- Conversation list -->
            <div class="chat-sidebar">
              <div class="chat-sidebar__header">Conversații</div>
              <div class="chat-sidebar__list">
                <button
                  v-for="conv in conversations" :key="conv.id"
                  class="conv-item"
                  :class="{ 'conv-item--active': selectedConversation === conv.id }"
                  @click="selectedConversation = conv.id; fetchMessages()"
                >
                  <div class="conv-item__avatar">{{ conv.client_name?.[0]?.toUpperCase() }}</div>
                  <div class="conv-item__info">
                    <div class="conv-item__name">{{ conv.client_name }}</div>
                    <div class="conv-item__date">{{ new Date(conv.created_at).toLocaleDateString('ro-RO') }}</div>
                  </div>
                </button>
                <div v-if="!conversations.length" class="empty-state empty-state--sm">
                  <i class="fas fa-comment-slash"></i><p>Nicio conversație</p>
                </div>
              </div>
            </div>

            <!-- Messages -->
            <div class="chat-main">
              <div class="chat-messages" ref="chatBox">
                <div
                  v-for="(msg, i) in messages" :key="i"
                  class="msg-row"
                  :class="msg.from === 'admin' ? 'msg-row--right' : 'msg-row--left'"
                >
                  <div class="bubble" :class="msg.from === 'admin' ? 'bubble--admin' : 'bubble--client'">
                    <div class="bubble__text">{{ msg.message }}</div>
                    <div v-if="msg.attachments?.length" class="bubble__attachments">
                      <a v-for="f in msg.attachments" :key="f.url" :href="cleanUrl(f.url)" target="_blank" class="attach-link">
                        <i class="fas fa-paperclip"></i> {{ f.title }}
                      </a>
                    </div>
                    <div class="bubble__time">{{ formatDate(msg.timestamp) }}</div>
                  </div>
                </div>
                <div v-if="!messages.length" class="empty-state"><i class="fas fa-comments"></i><p>Niciun mesaj.</p></div>
              </div>
              <div class="chat-input">
                <input v-model="message" class="chat-input__text" placeholder="Scrie un mesaj…" @keyup.enter="sendMessage" />
                <button class="btn btn--primary" @click="sendMessage"><i class="fas fa-paper-plane"></i></button>
                <label class="chat-input__file" title="Atașează fișier">
                  <i class="fas fa-paperclip"></i>
                  <input type="file" @change="handleFileUpload" style="display:none" />
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- ── CLIENTS ── -->
        <div v-else-if="activeTab === 'clients'" class="fade-in">
          <div class="section-header">
            <h2 class="section-title">Clienți <span class="count-pill">{{ clientList.length }}</span></h2>
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input v-model="clientSearch" placeholder="Caută…" />
            </div>
          </div>
          <div class="table-card">
            <table class="data-table">
              <thead>
                <tr><th>Nume</th><th>Email</th><th>Telefon</th><th>Înregistrat</th><th style="width:60px"></th></tr>
              </thead>
              <tbody>
                <tr v-for="c in paginatedClients" :key="c.id">
                  <td class="fw">{{ c.full_name }}</td>
                  <td class="muted small">{{ c.email }}</td>
                  <td class="muted small">{{ c.phone }}</td>
                  <td class="muted small">{{ formatDate(c.created_at) }}</td>
                  <td>
                    <button class="icon-btn icon-btn--danger" @click="deleteClient(c.id)" title="Șterge client">
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!filteredClients.length" class="empty-state"><i class="fas fa-user-slash"></i><p>Niciun client găsit.</p></div>
          </div>
          <div class="pagination" v-if="totalClientPages > 1">
            <button class="page-btn" :disabled="clientPage === 1" @click="clientPage--"><i class="fas fa-chevron-left"></i></button>
            <button
              v-for="p in totalClientPages" :key="p"
              class="page-btn"
              :class="{ 'page-btn--active': p === clientPage }"
              @click="clientPage = p"
            >{{ p }}</button>
            <button class="page-btn" :disabled="clientPage === totalClientPages" @click="clientPage++"><i class="fas fa-chevron-right"></i></button>
          </div>
        </div>

        <!-- ── CONSULTANCY ── -->
        <div v-else-if="activeTab === 'consultancy'" class="fade-in">
          <div class="section-header">
            <h2 class="section-title">Cereri <span class="count-pill">{{ requests.length }}</span></h2>
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input v-model="reqSearch" placeholder="Caută…" />
            </div>
          </div>
          <div class="table-card">
            <table class="data-table data-table--consultancy">
              <thead>
                <tr><th>Nume</th><th>Contact</th><th>Interval</th><th>Tip / Mesaj</th><th>Fișiere</th><th>Status</th><th style="width:60px"></th></tr>
              </thead>
              <tbody>
                <tr v-for="req in paginatedRequests" :key="req.id">
                  <td class="fw">{{ req.full_name }}</td>
                  <td class="small muted"><div>{{ req.email }}</div><div>{{ req.phone }}</div></td>
                  <td class="small muted">{{ req.preferred_time || '—' }}</td>
                  <td>
                    <div class="fw small">{{ req.request_type }}</div>
                    <button class="msg-peek" @click="openMessageModal(req)" title="Vezi mesajul complet">
                      <i class="fas fa-eye"></i>
                      <span class="truncate" style="max-width:120px">{{ req.message }}</span>
                    </button>
                  </td>
                  <td>
                    <div v-if="req.documents?.length">
                      <a v-for="doc in req.documents" :key="doc.file" :href="doc.file" target="_blank" class="dl-link">
                        <i class="fas fa-download"></i> {{ doc.title }}
                      </a>
                    </div>
                    <span v-else class="muted">—</span>
                  </td>
                  <td>
                    <select class="status-select" v-model="req.status" @change="updateStatus(req)">
                      <option value="new">Nouă</option>
                      <option value="in_progress">În analiză</option>
                      <option value="done">Finalizată</option>
                      <option value="rejected">Respinsă</option>
                    </select>
                  </td>
                  <td>
                    <button class="icon-btn icon-btn--danger" @click="deleteRequest(req.id)"><i class="fas fa-trash-alt"></i></button>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!filteredRequests.length" class="empty-state"><i class="fas fa-folder-open"></i><p>Nicio cerere trimisă.</p></div>
          </div>
          <div class="pagination" v-if="totalReqPages > 1">
            <button class="page-btn" :disabled="reqPage === 1" @click="reqPage--"><i class="fas fa-chevron-left"></i></button>
            <button
              v-for="p in totalReqPages" :key="p"
              class="page-btn"
              :class="{ 'page-btn--active': p === reqPage }"
              @click="reqPage = p"
            >{{ p }}</button>
            <button class="page-btn" :disabled="reqPage === totalReqPages" @click="reqPage++"><i class="fas fa-chevron-right"></i></button>
          </div>
        </div>

      </main>
    </div>

    <!-- ═══ MODAL: Add Admin ═══ -->
    <teleport to="body">
      <div v-if="showAddAdminModal" class="modal-overlay" @click.self="showAddAdminModal = false">
        <div class="modal-box">
          <div class="modal-box__header">
            <span><i class="fas fa-user-plus"></i> Adaugă administrator</span>
            <button class="modal-box__close" @click="showAddAdminModal = false"><i class="fas fa-times"></i></button>
          </div>
          <form @submit.prevent="createAdmin" class="modal-box__body">
            <div class="field"><label class="field__label">Username</label><input v-model="newAdmin.username" class="field__input" required /></div>
            <div class="field"><label class="field__label">Parolă</label><input type="password" v-model="newAdmin.password" class="field__input" required /></div>
            <button type="submit" class="btn btn--primary btn--full"><i class="fas fa-save"></i> Salvează</button>
          </form>
        </div>
      </div>

      <!-- Edit Admin Modal -->
      <div v-if="showEditAdminModal" class="modal-overlay" @click.self="showEditAdminModal = false">
        <div class="modal-box">
          <div class="modal-box__header">
            <span><i class="fas fa-user-edit"></i> Editează administrator</span>
            <button class="modal-box__close" @click="showEditAdminModal = false"><i class="fas fa-times"></i></button>
          </div>
          <form @submit.prevent="updateAdmin" class="modal-box__body">
            <div class="field"><label class="field__label">Username</label><input v-model="editAdminData.username" class="field__input" required /></div>
            <div class="field"><label class="field__label">Parolă nouă <span class="opt">(lasă gol pentru a păstra)</span></label><input type="password" v-model="editAdminData.password" class="field__input" /></div>
            <button type="submit" class="btn btn--primary btn--full"><i class="fas fa-sync-alt"></i> Actualizează</button>
          </form>
        </div>
      </div>

      <!-- Edit News Modal -->
      <div v-if="showEditNewsModal" class="modal-overlay" @click.self="showEditNewsModal = false">
        <div class="modal-box modal-box--lg">
          <div class="modal-box__header">
            <span><i class="fas fa-edit"></i> Editează anunț</span>
            <button class="modal-box__close" @click="showEditNewsModal = false"><i class="fas fa-times"></i></button>
          </div>
          <form @submit.prevent="submitEditNews" class="modal-box__body">
            <div class="field"><label class="field__label">Titlu</label><input v-model="editNews.title" class="field__input" required /></div>
            <div class="field"><label class="field__label">Conținut</label><textarea v-model="editNews.content" class="field__input field__textarea" rows="5" required></textarea></div>
            <div class="field"><label class="field__label">Imagine nouă <span class="opt">(opțional)</span></label><input type="file" @change="onEditImageChange" accept="image/*" class="field__file" /></div>
            <div class="field"><label class="field__label">Fișiere noi</label><input type="file" multiple @change="onEditFilesChange" class="field__file" /></div>
            <div class="field field--row">
              <input type="checkbox" id="pubCheck" v-model="editNews.published" class="field__check" />
              <label for="pubCheck" class="field__label mb-0">Publicat</label>
            </div>
            <button type="submit" class="btn btn--primary btn--full"><i class="fas fa-sync-alt"></i> Actualizează</button>
          </form>
        </div>
      </div>

      <!-- Message preview modal -->
      <div v-if="showMsgModal" class="modal-overlay" @click.self="showMsgModal = false">
        <div class="modal-box">
          <div class="modal-box__header">
            <span><i class="fas fa-envelope-open-text"></i> Mesaj cerere</span>
            <button class="modal-box__close" @click="showMsgModal = false"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-box__body">
            <div class="msg-meta">
              <span class="msg-meta__chip">{{ selectedReq.request_type }}</span>
              <span class="msg-meta__name">{{ selectedReq.full_name }}</span>
            </div>
            <div class="msg-full">{{ selectedReq.message }}</div>
            <div v-if="selectedReq.preferred_time" class="msg-time-row">
              <i class="fas fa-clock"></i> Interval preferat: <strong>{{ selectedReq.preferred_time }}</strong>
            </div>
          </div>
        </div>
      </div>
    </teleport>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from 'vue'
import api from '@/api/axios'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'

const router    = useRouter()
const activeTab = ref('home')
const sidebarOpen = ref(false)
const formatDate= (d) => d ? new Date(d).toLocaleString('ro-RO') : '—'

const tabs = computed(() => [
  { key: 'home',        label: 'Acasă',   icon: 'fas fa-home',     badge: null },
  { key: 'news',        label: 'Anunțuri',icon: 'fas fa-bullhorn', badge: newsList.value.length || null },
  { key: 'chat',        label: 'Mesaje',  icon: 'fas fa-comments', badge: null },
  { key: 'clients',     label: 'Clienți', icon: 'fas fa-users',    badge: clientList.value.length || null },
  { key: 'consultancy', label: 'Cereri',  icon: 'fas fa-file-alt', badge: requests.value.filter(r => r.status === 'new').length || null },
])

const currentTabLabel = computed(() => tabs.value.find(t => t.key === activeTab.value)?.label || '')

// ── stats ──
const statsList = ref([])
onMounted(async () => {
  if (localStorage.getItem('admin_token') !== 'admin_fake_token') router.push('/admin/login')
  const res = await api.get('admin-dashboard-metrics/')
  statsList.value = [
    { label: 'Clienți',            value: res.data.total_clients,  icon: 'fas fa-users',    color: 'rgb(0, 75, 107)' },
    { label: 'Mesaje',             value: res.data.total_messages, icon: 'fas fa-comments', color: 'rgb(0, 100, 140)' },
    { label: 'Anunțuri',           value: res.data.total_news,     icon: 'fas fa-bullhorn', color: 'rgb(0, 55, 80)' },
    { label: 'Cereri consultanță', value: res.data.total_requests, icon: 'fas fa-file-alt', color: 'rgb(0, 120, 160)' },
  ]
})

// ── admins ──
const adminList          = ref([])
const showAddAdminModal  = ref(false)
const showEditAdminModal = ref(false)
const newAdmin           = ref({ username: '', password: '' })
const editAdminData      = ref({ id: null, username: '', password: '' })

const fetchAdmins = async () => { const r = await api.get('/admin-list/'); adminList.value = r.data }
onMounted(fetchAdmins)

const createAdmin = async () => {
  try {
    await api.post('/create-admin/', newAdmin.value)
    showAddAdminModal.value = false; newAdmin.value = { username: '', password: '' }
    await fetchAdmins(); Swal.fire('Succes!', 'Administrator creat.', 'success')
  } catch (e) { Swal.fire('Eroare!', e.response?.data?.error || 'Ceva n-a mers.', 'error') }
}
const editAdmin = (admin) => {
  editAdminData.value = { id: admin.id, username: admin.username, password: '' }
  showEditAdminModal.value = true
}
const updateAdmin = async () => {
  try {
    await api.post('/update-admin/', editAdminData.value)
    showEditAdminModal.value = false; await fetchAdmins()
    Swal.fire('Actualizat!', 'Datele au fost modificate.', 'success')
  } catch { Swal.fire('Eroare!', 'Nu s-a putut actualiza.', 'error') }
}
const deleteAdmin = async (id) => {
  if (adminList.value.length === 1) { Swal.fire({ icon: 'warning', title: 'Nu poți șterge ultimul administrator!' }); return }
  const c = await Swal.fire({ title: 'Ești sigur?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444', confirmButtonText: 'Da, șterge-l!' })
  if (c.isConfirmed) { await api.delete(`/delete-admin/${id}/`); await fetchAdmins(); Swal.fire('Șters!', '', 'success') }
}
const logoutAdmin = () => { localStorage.removeItem('admin_token'); router.push('/admin/login') }

// ── news ──
const form              = ref({ title: '', content: '', image: null, files: [] })
const newsList          = ref([])
const showEditNewsModal = ref(false)
const editNews          = ref({ id: null, title: '', content: '', published: true, image: null, files: [] })

const onImageChange     = (e) => { form.value.image = e.target.files[0] }
const onFilesChange     = (e) => { form.value.files = [...e.target.files] }
const onEditImageChange = (e) => { editNews.value.image = e.target.files[0] }
const onEditFilesChange = (e) => { editNews.value.files = [...e.target.files] }

const fetchNews = async () => { const r = await api.get('/news-posts/'); newsList.value = r.data }
onMounted(fetchNews)

const createNewsPost = async () => {
  const data = new FormData()
  data.append('title', form.value.title); data.append('content', form.value.content)
  if (form.value.image) data.append('image', form.value.image)
  form.value.files.forEach(f => data.append('documents', f))
  try {
    await api.post('/create-news-post/', data)
    form.value = { title: '', content: '', image: null, files: [] }
    await fetchNews(); Swal.fire('Succes!', 'Anunțul a fost publicat.', 'success')
  } catch (e) { Swal.fire('Eroare!', e.response?.data?.error || '', 'error') }
}
const deleteNews = async (id) => {
  const c = await Swal.fire({ title: 'Ești sigur?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444', confirmButtonText: 'Da, șterge-l!' })
  if (c.isConfirmed) { await api.delete(`/delete-news/${id}/`); await fetchNews() }
}
const openEditNewsModal = (news) => {
  editNews.value = { id: news.id, title: news.title, content: news.content, published: news.published ?? true, image: null, files: [] }
  showEditNewsModal.value = true
}
const submitEditNews = async () => {
  const data = new FormData()
  data.append('title', editNews.value.title); data.append('content', editNews.value.content)
  data.append('published', editNews.value.published)
  if (editNews.value.image) data.append('image', editNews.value.image)
  editNews.value.files.forEach(f => data.append('documents', f))
  try {
    await api.put(`/edit-news/${editNews.value.id}/`, data)
    await fetchNews(); showEditNewsModal.value = false
    Swal.fire('Succes!', 'Anunț actualizat.', 'success')
  } catch { Swal.fire('Eroare!', 'Nu s-au putut salva modificările.', 'error') }
}

// ── chat ──
const chatBox              = ref(null)
const conversations        = ref([])
const selectedConversation = ref(null)
const messages             = ref([])
const message              = ref('')

const fetchConversations = async () => {
  const r = await api.get('/admin/conversations/'); conversations.value = r.data
  selectedConversation.value = r.data[0]?.id || null
  if (selectedConversation.value) fetchMessages()
}
const fetchMessages = async () => {
  if (!selectedConversation.value) return
  const r = await api.get(`/admin/messages/${selectedConversation.value}/`); messages.value = r.data
  await nextTick(); if (chatBox.value) chatBox.value.scrollTop = chatBox.value.scrollHeight
}
const sendMessage = async () => {
  if (!message.value.trim()) return
  await api.post(`/admin/messages/${selectedConversation.value}/send/`, { message: message.value })
  message.value = ''; fetchMessages()
}
const handleFileUpload = async (e) => {
  const file = e.target.files[0]; if (!file) return
  const fd = new FormData(); fd.append('file', file)
  await api.post(`/admin/messages/${selectedConversation.value}/upload/`, fd)
  e.target.value = ''; fetchMessages()
}
const cleanUrl = (url) => { const base = api.defaults.baseURL.replace(/\/api\/?$/, ''); return `${base}${url}` }
onMounted(fetchConversations)

// ── clients ──
const clientList   = ref([])
const clientSearch = ref('')
const clientPage   = ref(1)
const PAGE_SIZE    = 10

const fetchClients = async () => { const r = await api.get('/admin/clients/'); clientList.value = r.data }
onMounted(fetchClients)

const deleteClient = async (id) => {
  const c = await Swal.fire({
    title: 'Ștergi clientul?',
    text: 'Se vor șterge și toate conversațiile asociate.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonText: 'Anulează',
    confirmButtonText: 'Da, șterge!'
  })
  if (c.isConfirmed) {
    try {
      await api.delete(`/admin/clients/${id}/delete/`)
      await fetchClients()
      Swal.fire('Șters!', 'Clientul și conversațiile au fost șterse.', 'success')
    } catch { Swal.fire('Eroare!', 'Nu s-a putut șterge.', 'error') }
  }
}

const filteredClients = computed(() => {
  const q = clientSearch.value.toLowerCase()
  return q ? clientList.value.filter(c => c.full_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)) : clientList.value
})
const totalClientPages = computed(() => Math.ceil(filteredClients.value.length / PAGE_SIZE))
const paginatedClients = computed(() => {
  const start = (clientPage.value - 1) * PAGE_SIZE
  return filteredClients.value.slice(start, start + PAGE_SIZE)
})
watch(clientSearch, () => { clientPage.value = 1 })

// ── consultancy ──
const requests  = ref([])
const reqSearch = ref('')
const reqPage   = ref(1)

const fetchConsultancyRequests = async () => { const r = await api.get('/consultancy-requests/'); requests.value = r.data }
const updateStatus = async (req) => {
  try { await api.post(`/update-request-status/${req.id}/`, { status: req.status }); Swal.fire('Succes!', 'Status actualizat.', 'success') }
  catch { Swal.fire('Eroare!', '', 'error') }
}
const deleteRequest = async (id) => {
  const c = await Swal.fire({ title: 'Ești sigur?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444', confirmButtonText: 'Da, șterge!' })
  if (c.isConfirmed) { await api.delete(`/delete-consultancy-request/${id}/`); await fetchConsultancyRequests() }
}
onMounted(fetchConsultancyRequests)

// ── message preview modal ──
const showMsgModal = ref(false)
const selectedReq  = ref({})
const openMessageModal = (req) => { selectedReq.value = req; showMsgModal.value = true }

const filteredRequests = computed(() => {
  const q = reqSearch.value.toLowerCase()
  return q ? requests.value.filter(r => r.full_name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q)) : requests.value
})
const totalReqPages   = computed(() => Math.ceil(filteredRequests.value.length / PAGE_SIZE))
const paginatedRequests = computed(() => {
  const start = (reqPage.value - 1) * PAGE_SIZE
  return filteredRequests.value.slice(start, start + PAGE_SIZE)
})
watch(reqSearch, () => { reqPage.value = 1 })
</script>

<style scoped>
/* ── Reset / Base ── */
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.admin-shell {
  display: flex;
  min-height: 100vh;
  background: #f8f9fc;
  font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
}

/* ── Sidebar ── */
.sidebar {
  width: 240px;
  min-height: 100vh;
  background: #fff;
  border-right: 1px solid #e8eaf0;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0; left: 0; bottom: 0;
  z-index: 100;
  transition: transform .25s ease;
}

.sidebar__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 22px 20px;
  border-bottom: 1px solid #f0f1f5;
}
.brand-icon {
  width: 36px; height: 36px;
  background: rgb(0, 75, 107);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 15px;
}
.brand-name { font-weight: 700; font-size: 17px; color: #1e1f26; letter-spacing: -.3px; }

.sidebar__nav {
  flex: 1;
  padding: 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  width: 100%;
  text-align: left;
  transition: background .15s, color .15s;
}
.nav-item:hover { background: #f3f4f6; color: #111; }
.nav-item--active { background: #e6f0f5; color: rgb(0, 75, 107); font-weight: 600; }
.nav-item__icon { width: 20px; text-align: center; font-size: 13px; }
.nav-item__label { flex: 1; }
.nav-item__badge {
  background: rgb(0, 75, 107); color: #fff;
  font-size: 11px; font-weight: 700;
  padding: 1px 7px; border-radius: 20px;
}

.sidebar__logout {
  margin: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #fee2e2;
  background: #fff;
  color: #ef4444;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex; align-items: center; gap: 8px;
  transition: background .15s;
}
.sidebar__logout:hover { background: #fef2f2; }

.sidebar-overlay {
  display: none;
  position: fixed; inset: 0; z-index: 99;
  background: rgba(0,0,0,.3);
}

/* ── Main ── */
.main {
  flex: 1;
  margin-left: 240px;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* ── Topbar ── */
.topbar {
  position: sticky; top: 0; z-index: 50;
  background: #fff;
  border-bottom: 1px solid #e8eaf0;
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.topbar__hamburger {
  display: none;
  background: none; border: none; cursor: pointer;
  font-size: 18px; color: #4b5563;
}
.topbar__title { font-size: 16px; font-weight: 700; color: #1e1f26; flex: 1; }
.topbar__avatar {
  width: 34px; height: 34px;
  background: rgb(0, 75, 107);
  border-radius: 50%;
  color: #fff; font-weight: 700; font-size: 13px;
  display: flex; align-items: center; justify-content: center;
}

/* ── Content ── */
.content { padding: 24px; flex: 1; }

.fade-in { animation: fadeIn .2s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

/* ── Stats ── */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 28px;
}
.stat-card {
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 14px;
  padding: 18px;
  display: flex; align-items: center; gap: 14px;
}
.stat-card__icon {
  width: 46px; height: 46px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 18px;
  flex-shrink: 0;
}
.stat-card__value { font-size: 26px; font-weight: 800; color: #1e1f26; line-height: 1; }
.stat-card__label { font-size: 12px; color: #9ca3af; margin-top: 2px; }

/* ── Section header ── */
.section-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px; gap: 12px; flex-wrap: wrap;
}
.section-title { font-size: 16px; font-weight: 700; color: #1e1f26; display: flex; align-items: center; gap: 8px; }
.count-pill { background: #e6f0f5; color: rgb(0, 75, 107); font-size: 12px; font-weight: 700; padding: 2px 8px; border-radius: 20px; }
.search-box {
  display: flex; align-items: center; gap: 8px;
  background: #fff; border: 1px solid #e0e2ea; border-radius: 8px;
  padding: 6px 12px;
}
.search-box i { color: #9ca3af; font-size: 13px; }
.search-box input { border: none; outline: none; font-size: 13px; color: #374151; background: transparent; width: 160px; }

/* ── Table Card ── */
.table-card {
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 16px;
}
.table-card--flush { border-radius: 0 0 14px 14px; border-top: none; }

.data-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table thead tr { background: #f8f9fc; }
.data-table th { padding: 11px 16px; text-align: left; font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; border-bottom: 1px solid #e8eaf0; }
.data-table td { padding: 13px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; vertical-align: middle; }
.data-table tbody tr:last-child td { border-bottom: none; }
.data-table tbody tr:hover { background: #fafbff; }

/* ── Utilities ── */
.fw { font-weight: 600; color: #111827; }
.muted { color: #9ca3af; }
.small { font-size: 12.5px; }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* ── Badges ── */
.badge {
  display: inline-block;
  padding: 2px 10px; border-radius: 20px;
  font-size: 11px; font-weight: 600;
}
.badge--green { background: #dcfce7; color: #166534; }
.badge--gray  { background: #f3f4f6; color: #6b7280; }

/* ── Buttons ── */
.btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 8px; border: none;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  transition: opacity .15s, transform .1s;
}
.btn:active { transform: scale(.97); }
.btn--primary { background: rgb(0, 75, 107); color: #fff; }
.btn--primary:hover { background: rgb(0, 60, 87); }
.btn--sm { padding: 6px 12px; font-size: 13px; }
.btn--full { width: 100%; justify-content: center; }

.action-btns { display: flex; gap: 6px; align-items: center; }
.icon-btn {
  width: 30px; height: 30px; border-radius: 7px; border: none;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; cursor: pointer; transition: background .15s;
}
.icon-btn--ghost { background: #f3f4f6; color: #6b7280; }
.icon-btn--ghost:hover { background: #e5e7eb; }
.icon-btn--danger { background: #fee2e2; color: #ef4444; }
.icon-btn--danger:hover { background: #fecaca; }

/* ── Fields ── */
.field { margin-bottom: 16px; }
.field--row { display: flex; align-items: center; gap: 8px; }
.field__label { display: block; font-size: 12.5px; font-weight: 600; color: #374151; margin-bottom: 5px; }
.opt { font-weight: 400; color: #9ca3af; }
.field__input {
  width: 100%; padding: 9px 12px;
  border: 1px solid #e0e2ea; border-radius: 8px;
  font-size: 13.5px; color: #111827; background: #fafbfc;
  outline: none; transition: border .15s;
}
.field__input:focus { border-color: rgb(0, 75, 107); background: #fff; }
.field__textarea { min-height: 110px; resize: vertical; }
.field__file {
  width: 100%; font-size: 13px; color: #6b7280;
  border: 1.5px dashed #d1d5db; border-radius: 8px;
  padding: 8px 12px; background: #fafbfc; cursor: pointer;
}
.field__check { width: 16px; height: 16px; cursor: pointer; accent-color: rgb(0, 75, 107); }

/* ── Panel (two-col news layout) ── */
.two-col { display: grid; grid-template-columns: 1fr 1.6fr; gap: 20px; }
.panel { background: #fff; border: 1px solid #e8eaf0; border-radius: 14px; overflow: hidden; }
.panel__header { padding: 14px 18px; font-size: 14px; font-weight: 700; color: rgb(0, 75, 107); border-bottom: 1px solid #f0f1f5; display: flex; align-items: center; gap: 8px; }
.panel__body { padding: 18px; }

/* ── Chat ── */
.chat-layout { display: grid; grid-template-columns: 240px 1fr; gap: 16px; height: calc(100vh - 140px); }
.chat-sidebar { background: #fff; border: 1px solid #e8eaf0; border-radius: 14px; display: flex; flex-direction: column; overflow: hidden; }
.chat-sidebar__header { padding: 14px 16px; font-size: 13px; font-weight: 700; color: #374151; border-bottom: 1px solid #f0f1f5; }
.chat-sidebar__list { flex: 1; overflow-y: auto; }

.conv-item { width: 100%; display: flex; align-items: center; gap: 10px; padding: 12px 14px; border: none; background: none; cursor: pointer; text-align: left; border-bottom: 1px solid #f5f6fa; transition: background .15s; }
.conv-item:hover { background: #f8f9fc; }
.conv-item--active { background: #e6f0f5; }
.conv-item__avatar { width: 34px; height: 34px; border-radius: 50%; background: rgb(0, 75, 107); color: #fff; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.conv-item__name { font-size: 13px; font-weight: 600; color: #111827; }
.conv-item__date { font-size: 11px; color: #9ca3af; margin-top: 2px; }

.chat-main { background: #fff; border: 1px solid #e8eaf0; border-radius: 14px; display: flex; flex-direction: column; overflow: hidden; }
.chat-messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 10px; }
.msg-row { display: flex; }
.msg-row--right { justify-content: flex-end; }
.msg-row--left  { justify-content: flex-start; }

.bubble { max-width: 70%; padding: 10px 14px; border-radius: 14px; font-size: 13.5px; line-height: 1.5; }
.bubble--admin  { background: rgb(0, 75, 107); color: #fff; border-bottom-right-radius: 4px; }
.bubble--client { background: #f0f1f5; color: #111827; border-bottom-left-radius: 4px; }
.bubble__time { font-size: 11px; opacity: .6; margin-top: 4px; text-align: right; }
.bubble__attachments { margin-top: 6px; display: flex; flex-direction: column; gap: 3px; }
.attach-link { font-size: 12px; color: rgba(255,255,255,.8); text-decoration: underline; }

.chat-input { padding: 12px 14px; border-top: 1px solid #f0f1f5; display: flex; gap: 8px; align-items: center; }
.chat-input__text { flex: 1; padding: 9px 14px; border: 1px solid #e0e2ea; border-radius: 8px; font-size: 13.5px; outline: none; }
.chat-input__text:focus { border-color: rgb(0, 75, 107); }
.chat-input__file {
  width: 36px; height: 36px; border-radius: 8px; background: #f3f4f6;
  color: #6b7280; display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 14px; transition: background .15s;
}
.chat-input__file:hover { background: #e5e7eb; }

/* ── Consultancy ── */
.status-select {
  font-size: 12.5px; padding: 5px 8px;
  border: 1px solid #e0e2ea; border-radius: 7px;
  background: #fafbfc; color: #374151; cursor: pointer; outline: none;
}
.dl-link { display: block; font-size: 12px; color: rgb(0, 75, 107); margin-bottom: 3px; text-decoration: none; }
.dl-link:hover { text-decoration: underline; }

/* ── Pagination ── */
.pagination { display: flex; justify-content: center; align-items: center; gap: 4px; padding: 8px 0; }
.page-btn {
  min-width: 34px; height: 34px; border-radius: 8px;
  border: 1px solid #e0e2ea; background: #fff;
  font-size: 13px; font-weight: 500; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #374151; transition: all .15s;
}
.page-btn:hover:not(:disabled) { background: #e6f0f5; border-color: rgb(0, 75, 107); color: rgb(0, 75, 107); }
.page-btn--active { background: rgb(0, 75, 107); border-color: rgb(0, 75, 107); color: #fff; font-weight: 700; }
.page-btn:disabled { opacity: .35; cursor: not-allowed; }

/* ── Empty state ── */
.empty-state { padding: 40px 20px; text-align: center; color: #9ca3af; }
.empty-state i { font-size: 28px; margin-bottom: 10px; display: block; }
.empty-state p { font-size: 13.5px; }
.empty-state--sm { padding: 20px; }
.empty-state--sm i { font-size: 20px; margin-bottom: 6px; }

/* ── Modals ── */
.modal-overlay {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(15,18,35,.45);
  display: flex; align-items: center; justify-content: center;
  padding: 20px;
  animation: fadeIn .15s ease;
}
.modal-box {
  background: #fff; border-radius: 16px;
  width: 100%; max-width: 420px;
  box-shadow: 0 20px 60px rgba(0,0,0,.15);
  animation: slideUp .2s ease;
}
.modal-box--lg { max-width: 580px; }
.modal-box__header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px 0;
  font-size: 15px; font-weight: 700; color: rgb(0, 75, 107);
}
.modal-box__close { background: none; border: none; cursor: pointer; color: #9ca3af; font-size: 16px; }
.modal-box__close:hover { color: #374151; }
.modal-box__body { padding: 18px 22px 22px; }

@keyframes slideUp { from { transform: translateY(16px); opacity: 0; } to { transform: none; opacity: 1; } }

/* ── Mobile ── */
@media (max-width: 900px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .two-col { grid-template-columns: 1fr; }
  .chat-layout { grid-template-columns: 1fr; height: auto; }
  .chat-sidebar { max-height: 200px; }
  .chat-main { height: 480px; }
}

@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
    box-shadow: 4px 0 20px rgba(0,0,0,.1);
  }
  .sidebar--open { transform: translateX(0); }
  .sidebar-overlay { display: block; }
  .main { margin-left: 0; }
  .topbar__hamburger { display: flex; }
  .content { padding: 16px; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
}

@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr 1fr; }
  .data-table--consultancy th:nth-child(3),
  .data-table--consultancy td:nth-child(3) { display: none; }
  .section-header { flex-direction: column; align-items: flex-start; }
  .search-box input { width: 120px; }
}

/* ── Message peek button ── */
.msg-peek {
  display: inline-flex; align-items: center; gap: 6px;
  background: #f0f4f8; border: none; border-radius: 6px;
  padding: 4px 8px; cursor: pointer; color: #374151;
  font-size: 12px; max-width: 100%; overflow: hidden;
  transition: background .15s;
}
.msg-peek:hover { background: #e6f0f5; color: rgb(0,75,107); }
.msg-peek i { flex-shrink: 0; color: rgb(0,75,107); }

/* ── Message modal content ── */
.msg-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 14px; }
.msg-meta__chip {
  background: #e6f0f5; color: rgb(0,75,107);
  font-size: 11px; font-weight: 700;
  padding: 3px 10px; border-radius: 20px;
}
.msg-meta__name { font-size: 13px; color: #6b7280; }
.msg-full {
  background: #f8f9fc; border: 1px solid #e8eaf0;
  border-radius: 10px; padding: 14px 16px;
  font-size: 14px; line-height: 1.65; color: #1e1f26;
  white-space: pre-wrap; word-break: break-word;
  margin-bottom: 12px;
}
.msg-time-row { font-size: 12.5px; color: #9ca3af; display: flex; align-items: center; gap: 6px; }
.msg-time-row strong { color: #374151; }
</style>